import { Autocomplete, Dialog, DialogActions, DialogContent, DialogTitle, Stack, TextField } from "@mui/material";
import { Save } from "@mui/icons-material";

import * as yup from "yup";
import { toast } from "sonner";
import { useEffect } from "react";
import { Controller, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { theme } from "../../../theme/theme";
import { LoadingButton } from "@mui/lab";

import { useCreateEditContractorsMutation } from "../../../features/api masterlist/contractors_api/contractorsApi";
import { useLazyGetChannelsQuery } from "../../../features/api_channel_setup/channel/channelApi";

const schema = yup.object().shape({
  id: yup.string().nullable(),
  channelId: yup.object().required().label("Channel Name"),
  contractor: yup.string().required("Contractors is required."),
  pivotId: yup.string().nullable(),
});

export const ContractorsDialog = ({ data, open, onClose }) => {
  const [createEditContractors, { isLoading: createEditContractorsIsLoading, isFetching: createEditContractorsIsFetching }] = useCreateEditContractorsMutation();
  const [getChannel, { data: channelData, isLoading: channelIsLoading, isSuccess: channelIsSuccess }] = useLazyGetChannelsQuery();

  const {
    control,
    handleSubmit,
    register,
    watch,
    setValue,
    reset,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(schema),
    defaultValues: {
      id: null,
      channelId: null,
      contractor: "",
      pivotId: null,
    },
  });

  useEffect(() => {
    if (data) {
      setValue("id", data?.id);
      setValue("channelId", {
        id: data?.channelId,
        channel_Name: data?.channelName,
      });
      setValue("contractor", data?.contractor);
    }
  }, [data]);

  const onCloseAction = () => {
    reset();
    onClose();
  };

  const onSubmitAction = (formData) => {
    if (formData.id) {
      const editPayload = {
        id: data.id,
        channelId: formData?.channelId?.id,
        contractor: formData?.contractor,
        pivotId: data?.pivotId,
      };

      createEditContractors(editPayload)
        .unwrap()
        .then(() => {
          toast.success("Success!", {
            description: "Updated contractor! ",
            duration: 1500,
          });
          reset();
          onClose();
        })
        .catch((error) => {
          console.log("error: ", error);
          toast.error("Error!", {
            description: error.data.error.message,
            duration: 1500,
          });
        });
    } else {
      const addPayload = {
        channelId: formData?.channelId?.id,
        contractor: formData?.contractor,
      };

      createEditContractors(addPayload)
        .unwrap()
        .then(() => {
          toast.success("Success!", {
            description: "Contractor added successsfully!",
            duration: 1500,
          });
          reset();
          onClose();
        })
        .catch((error) => {
          console.log("error: ", error);
          toast.error("Error!", {
            description: error.data.error.message,
            duration: 1500,
          });
        });
    }
  };

  return (
    <>
      <Dialog fullWidth maxWidth="xs" open={open} PaperProps={{ style: { overflow: "unset" } }}>
        <DialogTitle
          sx={{
            display: "flex",
            justifyContent: "center",
            paddingTop: 0,
            paddingBottom: 0,
            marginBlockEnd: "5.28px",
            fontSize: "18px",
            fontWeight: 700,
            color: "#48BB78",
          }}
        >
          {data ? "Edit Contractor" : "Add Contractor"}
        </DialogTitle>

        <form onSubmit={handleSubmit(onSubmitAction)}>
          <DialogContent>
            <Stack sx={{ padding: "5px", gap: 1.5 }}>
              <Controller
                control={control}
                name="channelId"
                render={({ field: { ref, value, onChange } }) => {
                  return (
                    <Autocomplete
                      ref={ref}
                      value={value}
                      options={channelData?.value?.channel || []}
                      loading={channelIsLoading}
                      renderInput={(params) => <TextField label="Channel Name" fullWidth {...params} />}
                      onOpen={() => {
                        if (!channelIsSuccess)
                          getChannel({
                            Status: true,
                          });
                      }}
                      onChange={(_, value) => onChange(value)}
                      getOptionLabel={(option) => option.channel_Name}
                      isOptionEqualToValue={(option, value) => option.id === value.id}
                      fullWidth
                      disablePortal
                    />
                  );
                }}
              />

              <TextField
                {...register("contractor")}
                variant="outlined"
                label="Contractor Name"
                helperText={errors?.contractor?.message}
                error={!!errors?.contractor?.message}
                sx={{ borderColor: "primary" }}
                fullWidth
                autoComplete="off"
              />
            </Stack>
          </DialogContent>

          <DialogActions>
            <LoadingButton
              type="submit"
              variant="contained"
              loading={createEditContractorsIsLoading || createEditContractorsIsFetching}
              loadingPosition="start"
              startIcon={<Save />}
              disabled={!watch("channelId") || !watch("contractor")}
              sx={{
                ":disabled": {
                  backgroundColor: theme.palette.secondary.main,
                  color: "black",
                },
              }}
            >
              Save
            </LoadingButton>
            <LoadingButton variant="outlined" onClick={onCloseAction}>
              Close
            </LoadingButton>
          </DialogActions>
        </form>
      </Dialog>
    </>
  );
};
